import { Flex, Form } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SafetyCertificateOutlined, TableOutlined } from "@ant-design/icons";
import { ReactComponent as RefreshIcon } from "../../assets/Icons/RefreshIcon.svg";
import PanelCard from "../DashboardPage/components/PanelCard";
import RulesTable from "../../components/PermissionComp/RulesTable/RulesTable";
import CustomNotification from "../../Reuseable/CustomNotification/CustomNotification";
import NormalButton from "../../Reuseable/NormalButton/NormalButton";
import SearchBar from "../../Reuseable/SearchBar/SearchBar";
import CustomSelect from "../../Reuseable/Select/Select";
import {
  fetchAgencyProducts,
  fetchAgencyUsers,
  fetchAreaCodes,
} from "../../store/agencySlice";
import { fetchRulesList } from "../../store/rulesSlice";
import { fetchPortfolios } from "../../store/portfolioSlice";
import "./RulesPage.scss";

const RulesPage = () => {
  const dispatch = useDispatch();

  const { token } = useSelector((state) => state.auth);
  const { user_list, products_list, area_codes_list } = useSelector(
    (state) => state.agency
  );
  const { portfolios } = useSelector((state) => state.portfolio);
  const { rules_list = [], loading } = useSelector(
    (state) => state.rules
  );
  const [searchText, setSearchText] = useState("");

  const [createLoading, setCreateLoading] = useState(false);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    dispatch(fetchAgencyUsers());
    dispatch(fetchPortfolios());
    dispatch(fetchAgencyProducts());
    dispatch(fetchAreaCodes());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchRulesList({ page: 1, page_size: 10 }));
  }, [dispatch]);

  const handlePaginationChange = (page, pageSize) => {
    dispatch(fetchRulesList({ page, page_size: pageSize }));
  };

  const userListOption = user_list?.results?.map((item) => ({
    label: item.email,
    value: item.id,
  }));

  const bankListOption = portfolios?.map((item) => ({
    label: item.name,
    value: item.id,
  }));

  const productListOption = products_list?.results?.map((item) => ({
    label: item.products,
    value: item.id,
  }));

  const areaCodeListOption = area_codes_list?.map((item) => ({
    label: item.areacode,
    value: item.id,
  }));

  const bucketOptions = [
    { value: "1", label: "1" },
    { value: "2", label: "2" },
    { value: "3", label: "3" },
    { value: "4", label: "4" },
    { value: "5", label: "5" },
    { value: "6", label: "6" },
    { value: "7", label: "7" },
    { value: "8", label: "8" },
    { value: "8+", label: "8+" },
  ];

  const languageOptions = [
    { value: "English", label: "English" },
    { value: "Hindi", label: "Hindi" },
    { value: "Marathi", label: "Marathi" },
  ];

  const handleCreateRules = async (values) => {
    const payload = {
      user_id: values.team,
      bucket: values.bucket,
      portfolio: values.bank,
      product: values.product,
      area: values.area,
      language: values.language,
    };

    setCreateLoading(true);
    try {
      const res = await axios.post("agency/admin/create-permission/", payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (res?.status === 200) {
        CustomNotification.show("Rules created successfully!", "success");
        dispatch(fetchRulesList());
        form.resetFields();
      }
    } catch (error) {
      const message =
        error?.response?.data?.message || "Error creating rules";
      CustomNotification.show(message, "error");
      console.error(error);
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <Flex className="rules-page-main">
      <PanelCard
        title="Create New User Rules"
        icon={<SafetyCertificateOutlined />}
        accent="brand"
        className="whole-Select-container"
      >
        <Form
          form={form}
          style={{ width: "100%" }}
          layout="vertical"
          onFinish={handleCreateRules}
        >
          <Flex className="all-selectors">
            <Flex className="select-inner">
              <Form.Item
                name="team"
                label="Team"
                required={false}
                rules={[{ required: true, message: "Please select a team" }]}
              >
                <CustomSelect
                  placeholder="Select Team"
                  className="formSelect"
                  options={userListOption}
                />
              </Form.Item>

              <Form.Item
                name="bank"
                label="Banks"
                required={false}
                rules={[
                  {
                    required: true,
                    message: "Please select at least one bank",
                  },
                ]}
              >
                <CustomSelect
                  placeholder="Select Bank"
                  className="formSelect"
                  options={bankListOption}
                  isMultiSelect={true}
                />
              </Form.Item>
            </Flex>

            <Flex className="select-inner">
              <Form.Item
                name="product"
                label="Products"
                required={false}
                rules={[
                  {
                    required: true,
                    message: "Please select at least one product",
                  },
                ]}
              >
                <CustomSelect
                  placeholder="Select Product"
                  className="formSelect"
                  options={productListOption}
                  isMultiSelect={true}
                />
              </Form.Item>

              <Form.Item
                name="area"
                label="Operational Areas"
                required={false}
                rules={[
                  {
                    required: true,
                    message: "Please select at least one area",
                  },
                ]}
              >
                <CustomSelect
                  placeholder="Select Area"
                  className="formSelect"
                  options={areaCodeListOption}
                  isMultiSelect={true}
                />
              </Form.Item>
            </Flex>

            <Flex className="select-inner">
              <Form.Item
                name="bucket"
                label="Bucket"
                required={false}
                rules={[{ required: true, message: "Please select a bucket" }]}
              >
                <CustomSelect
                  placeholder="Select Bucket"
                  className="formSelect"
                  options={bucketOptions}
                />
              </Form.Item>

              <Form.Item
                name="language"
                label="Language"
                required={false}
                rules={[
                  { required: true, message: "Please select a language" },
                ]}
              >
                <CustomSelect
                  placeholder="Select Language"
                  className="formSelect"
                  options={languageOptions}
                />
              </Form.Item>
            </Flex>
          </Flex>

          <div className="rules-form-foot">
            <Form.Item shouldUpdate>
              {() => (
                <NormalButton
                  text={"Create Rules"}
                  onClick={() => form.submit()}
                  disabled={!form.getFieldValue("team") || createLoading}
                  loading={createLoading}
                />
              )}
            </Form.Item>
          </div>
        </Form>
      </PanelCard>

      <PanelCard
        title="Rules"
        icon={<TableOutlined />}
        accent="blue"
        padded={false}
        className="serch-and-table"
        actions={
          <div className="serch-continer">
            <SearchBar
            inputValue={searchText}
            placeholder="Search by user/email"
            onInputChange={(e) => setSearchText(e.target.value)}
            onSearch={() => {
              const query = {
                ...(searchText ? { search: searchText, page: 1, page_size: 10 } : { page: 1, page_size: 10 }),
              };
              dispatch(fetchRulesList(query));
              setPagination({
                current: 1,
                pageSize: 10,
                total: rules_list?.count || 0,
              });
            }}
            onCategoryChange={() => { }}
          />

            <button
              type="button"
              className="rules-refresh"
              title="Reset search and reload"
              onClick={() => {
                setSearchText("");
                dispatch(fetchRulesList({ page: 1, page_size: 10 }));
              }}
            >
              <RefreshIcon />
            </button>
          </div>
        }
      >
        <div className="rules-table-wrap">
          <RulesTable
            rules_list={
              Array.isArray(rules_list?.results) ? rules_list : {}
            }
            loading={loading}
            bankListOption={bankListOption}
            productListOption={productListOption}
            areaCodeListOption={areaCodeListOption}
            bucketOptions={bucketOptions}
            languageOptions={languageOptions}
            handlePaginationChange={handlePaginationChange}
          />
        </div>
      </PanelCard>
    </Flex>
  );
};

export default RulesPage;
